import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { create } from 'zustand';

import { SortingState } from '@tanstack/react-table';

import { DISCHARGE_CALENDAR_VIEWS } from 'components/dischargeCalendar/constants';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';

import { FilterState } from './types';

type DateRange = {
  startDate: Date;
  endDate: Date;
};

type WeekViewActions = {
  setWeekStart: (date: Date) => void;
  goToNextWeek: () => void;
  goToPreviousWeek: () => void;
  goToToday: () => void;
};

type MonthViewActions = {
  setMonthStart: (date: Date) => void;
  goToNextMonth: () => void;
  goToPreviousMonth: () => void;
  goToToday: () => void;
};

type DayViewActions = {
  setDay: (date: Date) => void;
  goToNextDay: () => void;
  goToPreviousDay: () => void;
  goToToday: () => void;
};

type ListViewActions = {
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setDateRange: (startDate: Date, endDate: Date) => void;
  setSort: (sort: SortingState) => void;
};

export type DischargeCalendarFilterState = FilterState<FilterValueArray | FilterValueBoolean>;

export type DischargeCalendarView = (typeof DISCHARGE_CALENDAR_VIEWS)[keyof typeof DISCHARGE_CALENDAR_VIEWS];

type FiltersActions = {
  setSearch: (value: string) => void;
  setFilter: (filterKey: string, values: FilterValueArray | FilterValueBoolean) => void;
  setFilters: (filters: DischargeCalendarFilterState) => void;
  removeFilter: (filterKey: string, id: string) => void;
  clearFilters: () => void;
  setCurrentView: (view: DischargeCalendarView) => void;
  setExpanded: (expanded: boolean) => void;
};

type State = {
  weekView: DateRange;
  monthView: DateRange;
  dayView: DateRange;
  listView: DateRange;
  listViewSort: SortingState;
  filters: DischargeCalendarFilterState;
  search: string;
  currentView: DischargeCalendarView;
  expanded: boolean;
  weekViewActions: WeekViewActions;
  monthViewActions: MonthViewActions;
  dayViewActions: DayViewActions;
  listViewActions: ListViewActions;
  filtersActions: FiltersActions;
  actions: {
    setExpanded: (expanded: boolean) => void;
  };
};

const getWeekRange = (date: Date): DateRange => {
  const start = startOfWeek(date, { weekStartsOn: 0 });
  const end = endOfWeek(start, { weekStartsOn: 0 });
  return { startDate: start, endDate: end };
};

const getMonthRange = (date: Date): DateRange => {
  const start = startOfMonth(date);
  const end = endOfMonth(start);
  return { startDate: start, endDate: end };
};

const getDayRange = (date: Date): DateRange => {
  const start = startOfDay(date);
  const end = endOfDay(start);
  return { startDate: start, endDate: end };
};

const today = new Date();

export const useDischargeCalendarStore = create<State>((set) => ({
  weekView: getWeekRange(today),
  monthView: getMonthRange(today),
  dayView: getDayRange(today),
  listView: getWeekRange(today),
  listViewSort: [{ id: 'projectedDischargeDate', desc: false }],
  filters: {},
  search: '',
  currentView: DISCHARGE_CALENDAR_VIEWS.WEEK,
  expanded: false,

  weekViewActions: {
    setWeekStart: (date) => set({ weekView: getWeekRange(date) }),
    goToNextWeek: () => set((state) => ({ weekView: getWeekRange(addWeeks(state.weekView.startDate, 1)) })),
    goToPreviousWeek: () => set((state) => ({ weekView: getWeekRange(subWeeks(state.weekView.startDate, 1)) })),
    goToToday: () => set({ weekView: getWeekRange(new Date()) }),
  },

  monthViewActions: {
    setMonthStart: (date) => set({ monthView: getMonthRange(date) }),
    goToNextMonth: () => set((state) => ({ monthView: getMonthRange(addMonths(state.monthView.startDate, 1)) })),
    goToPreviousMonth: () => set((state) => ({ monthView: getMonthRange(subMonths(state.monthView.startDate, 1)) })),
    goToToday: () => set({ monthView: getMonthRange(new Date()) }),
  },

  dayViewActions: {
    setDay: (date) => set({ dayView: getDayRange(date) }),
    goToNextDay: () => set((state) => ({ dayView: getDayRange(addDays(state.dayView.startDate, 1)) })),
    goToPreviousDay: () => set((state) => ({ dayView: getDayRange(subDays(state.dayView.startDate, 1)) })),
    goToToday: () => set({ dayView: getDayRange(new Date()) }),
  },

  listViewActions: {
    setStartDate: (date) => set((state) => ({ listView: { ...state.listView, startDate: date } })),
    setEndDate: (date) => set((state) => ({ listView: { ...state.listView, endDate: date } })),
    setDateRange: (startDate, endDate) => set({ listView: { startDate, endDate } }),
    setSort: (sort) => set({ listViewSort: sort }),
  },

  filtersActions: {
    setSearch: (value) => set({ search: value }),
    setFilter: (filterKey, values) =>
      set((state) => ({
        filters: {
          ...state.filters,
          [filterKey]: values,
        },
      })),
    setFilters: (filters) => set({ filters }),
    removeFilter: (filterKey, id) =>
      set((state) => ({
        filters: {
          ...state.filters,
          ...(state.filters[filterKey] ? { [filterKey]: state.filters[filterKey].removeFilter(id) } : {}),
        },
      })),
    clearFilters: () => set({ filters: {}, search: '' }),
    setCurrentView: (view) => set({ currentView: view }),
    setExpanded: (expanded) => set({ expanded }),
  },

  actions: {
    setExpanded: (expanded) => set({ expanded }),
  },
}));

export const useWeekViewState = () => useDischargeCalendarStore((state) => state.weekView);
export const useWeekViewActions = () => useDischargeCalendarStore((state) => state.weekViewActions);

export const useMonthViewState = () => useDischargeCalendarStore((state) => state.monthView);
export const useMonthViewActions = () => useDischargeCalendarStore((state) => state.monthViewActions);

export const useDayViewState = () => useDischargeCalendarStore((state) => state.dayView);
export const useDayViewActions = () => useDischargeCalendarStore((state) => state.dayViewActions);

export const useListViewState = () => useDischargeCalendarStore((state) => state.listView);
export const useListViewSort = () => useDischargeCalendarStore((state) => state.listViewSort);
export const useListViewActions = () => useDischargeCalendarStore((state) => state.listViewActions);

export const useDischargeCalendarFilters = () => useDischargeCalendarStore((state) => state.filters);
export const useDischargeCalendarSearch = () => useDischargeCalendarStore((state) => state.search);
export const useDischargeCalendarCurrentView = () => useDischargeCalendarStore((state) => state.currentView);
export const useDischargeCalendarExpanded = () => useDischargeCalendarStore((state) => state.expanded);
export const useDischargeCalendarFiltersActions = () => useDischargeCalendarStore((state) => state.filtersActions);
